import { Injectable, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { take, catchError, of } from 'rxjs';
import { PusherService } from './pusher.service';
import { JobsStatusService, JobStatus } from '@modules/settings/jobs-status/jobs-status.service';
import { JobsStatusActions } from '@modules/settings/jobs-status/state/actions';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { TsSnackbarService } from 'tsui';

interface PusherJobStatusPayload {
  account: number;
  error?: string;
  data: {
    job_status: {
      id: number;
      job_purpose: string;
      status: string;
      normalized_state: 'in_progress' | 'completed' | 'failed';
      progress_current: number | null;
      progress_total: number | null;
      progress_percent: number | null;
      progress_stage: string | null;
    };
  };
  user_id: number;
}

@Injectable({
  providedIn: 'root',
})
export class GlobalJobStatusService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly pusherService = inject(PusherService);
  private readonly jobsStatusService = inject(JobsStatusService);
  private readonly snackbarService = inject(TsSnackbarService);
  private readonly destroyRef = inject(DestroyRef);

  private accountId: number | null = null;
  private userId: number | null = null;

  initializeListeners(): void {
    const account = this.store.selectSnapshot(AccountState.getAccount);
    const user = this.store.selectSnapshot(UserState.getUser);

    if (account?.id && user?.id) {
      this.accountId = +account.id;
      this.userId = user.id;

      this.pusherService
        .listenToChannel<PusherJobStatusPayload>({
          channelName: `job-status-push-ts-user-${this.userId}`,
          eventName: 'job_status_update',
        })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((payload) => {
          console.log('User job status payload', payload);
          this.handleJobStatusUpdate(payload as PusherJobStatusPayload);
        });
    } else {
      console.log('No account or user found for job status listener');
    }
  }

  cleanup(): void {
    if (this.userId) {
      this.pusherService.closeChannel(`job-status-push-ts-user-${this.userId}`);
    }
  }

  private handleJobStatusUpdate(payload: PusherJobStatusPayload): void {
    if (!payload.data?.job_status) {
      return;
    }

    const jobStatusUpdate = payload.data.job_status;
    const jobId = jobStatusUpdate.id;

    const currentJobs = this.store.selectSnapshot<JobStatus[]>((state) => state.jobsStatusState?.jobs || []);
    const existingJob = currentJobs.find((job) => job.id === jobId);

    this.notifyJobStatus(jobStatusUpdate.job_purpose, jobStatusUpdate.normalized_state, payload.error);

    if (!existingJob || jobStatusUpdate.normalized_state === 'completed') {
      this.jobsStatusService
        .getJobStatusById(jobId)
        .pipe(
          take(1),
          catchError((error) => {
            console.error('Error fetching job status:', error);
            return of(jobStatusUpdate);
          }),
        )
        .subscribe((completeJob) => {
          if (!completeJob) {
            return;
          }

          if (existingJob) {
            this.store.dispatch(new JobsStatusActions.JobUpdated(jobId, completeJob as Partial<JobStatus>));
          } else {
            this.store.dispatch(new JobsStatusActions.JobAdded(completeJob as JobStatus));
          }
        });
    } else {
      const updatedJob: Partial<JobStatus> = {
        id: jobId,
        status: jobStatusUpdate.status as JobStatus['status'],
        ...(jobStatusUpdate.job_purpose && { object_name: jobStatusUpdate.job_purpose }),
      };

      this.store.dispatch(new JobsStatusActions.JobUpdated(jobId, updatedJob));
    }
  }

  private notifyJobStatus(purpose: string, status: string, error?: string): void {
    const snackbarOpts = { duration: 5000, horizontalPosition: 'right' as const, verticalPosition: 'top' as const };
    const navigateToJobs = () => this.router.navigate(['/settings/jobs-status']);

    switch (`${purpose}:${status}`) {
      case 'object_export:completed': {
        const ref = this.snackbarService.open('Export completed successfully', 'success', 'VIEW', {
          ...snackbarOpts,
          panelClass: ['ts-snackbar__export-success'],
        });
        ref.onAction().subscribe(navigateToJobs);
        break;
      }
      case 'object_export:failed': {
        const msg = error ? `Export failed: ${error}` : 'Export failed';
        this.snackbarService.open(msg, 'error', 'VIEW', snackbarOpts).onAction().subscribe(navigateToJobs);
        break;
      }
      case 'portfolio_creation:completed':
        this.snackbarService.open('Portfolio created successfully', 'success', undefined, snackbarOpts);
        break;
      case 'portfolio_creation:failed':
        this.snackbarService.open('Portfolio creation failed', 'error', undefined, snackbarOpts);
        break;
    }
  }
}
